import React, { useState, Component } from 'react';
import { motion } from 'framer-motion';
import { PlusIcon, SearchIcon, FilterIcon, SchoolIcon, CheckSquareIcon } from 'lucide-react';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import { AcademicYearSelector } from '../components/AcademicYearSelector';
import { ClassroomCard } from '../components/ClassroomCard';
import EmptyState from '../components/EmptyState';
import { CreateClassroomModal } from '../components/CreateClassroomModal';
import { BulkActionsBar } from '../components/BulkActionsBar';
import { BulkArchiveModal } from '../components/BulkArchiveModal';
import { RecreateClassroomsModal } from '../components/RecreateClassroomsModal';
import { EditClassroomModal } from '../components/EditClassroomModal';
import { ConfirmationModal } from '../components/ConfirmationModal';
// Mock Data
const MOCK_YEARS = [{
  id: '2024-2025',
  name: '2024-2025',
  startDate: '2024-08-01',
  endDate: '2025-06-30',
  isActive: true
}, {
  id: '2023-2024',
  name: '2023-2024',
  startDate: '2023-08-01',
  endDate: '2024-06-30',
  isActive: false
}, {
  id: '2022-2023',
  name: '2022-2023',
  startDate: '2022-08-01',
  endDate: '2023-06-30',
  isActive: false
}];
const MOCK_CLASSROOMS = [
// 2024-2025 Academic Year (Current - Active)
{
  id: '1',
  name: 'Grade 7A',
  grade: 'Grade 7',
  section: 'A',
  learnerCount: 24,
  educatorCount: 2,
  status: 'active' as const,
  type: 'organization' as const,
  academicYear: '2024-2025'
}, {
  id: '2',
  name: 'Grade 8B Science',
  grade: 'Grade 8',
  section: 'B',
  learnerCount: 28,
  educatorCount: 1,
  status: 'active' as const,
  type: 'organization' as const,
  academicYear: '2024-2025'
}, {
  id: '3',
  name: 'AP Physics',
  grade: 'Grade 11',
  section: '101',
  learnerCount: 15,
  educatorCount: 1,
  status: 'active' as const,
  type: 'organization' as const,
  academicYear: '2024-2025'
},
// 2023-2024 Academic Year (Previous - Archived)
{
  id: '4',
  name: 'Grade 7A',
  grade: 'Grade 7',
  section: 'A',
  learnerCount: 22,
  educatorCount: 2,
  status: 'archived' as const,
  type: 'organization' as const,
  academicYear: '2023-2024'
}, {
  id: '5',
  name: 'Grade 8B Science',
  grade: 'Grade 8',
  section: 'B',
  learnerCount: 26,
  educatorCount: 1,
  status: 'archived' as const,
  type: 'organization' as const,
  academicYear: '2023-2024'
}, {
  id: '6',
  name: 'AP Chemistry',
  grade: 'Grade 11',
  section: '102',
  learnerCount: 18,
  educatorCount: 1,
  status: 'archived' as const,
  type: 'organization' as const,
  academicYear: '2023-2024'
}, {
  id: '7',
  name: 'Grade 9 Math',
  grade: 'Grade 9',
  section: 'A',
  learnerCount: 30,
  educatorCount: 1,
  status: 'archived' as const,
  type: 'organization' as const,
  academicYear: '2023-2024'
},
// 2022-2023 Academic Year (Older - Archived)
{
  id: '8',
  name: 'Grade 7A',
  grade: 'Grade 7',
  section: 'A',
  learnerCount: 20,
  educatorCount: 2,
  status: 'archived' as const,
  type: 'organization' as const,
  academicYear: '2022-2023'
}, {
  id: '9',
  name: 'Grade 8A',
  grade: 'Grade 8',
  section: 'A',
  learnerCount: 25,
  educatorCount: 1,
  status: 'archived' as const,
  type: 'organization' as const,
  academicYear: '2022-2023'
}, {
  id: '10',
  name: 'AP Biology',
  grade: 'Grade 11',
  section: '103',
  learnerCount: 16,
  educatorCount: 1,
  status: 'archived' as const,
  type: 'organization' as const,
  academicYear: '2022-2023'
}];
export function AdminClassrooms() {
  const [selectedYearId, setSelectedYearId] = useState(MOCK_YEARS[0].id);
  const [activeTab, setActiveTab] = useState<'organization' | 'private'>('organization');
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  // Bulk Selection State
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedClassroomIds, setSelectedClassroomIds] = useState<Set<string>>(new Set());
  const [isBulkArchiveModalOpen, setIsBulkArchiveModalOpen] = useState(false);
  const [isRecreateModalOpen, setIsRecreateModalOpen] = useState(false);
  // Individual Classroom Actions State
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [classroomToRename, setClassroomToRename] = useState<any>(null);
  const [isArchiveConfirmOpen, setIsArchiveConfirmOpen] = useState(false);
  const [classroomToArchive, setClassroomToArchive] = useState<any>(null);
  // Filter logic
  const filteredClassrooms = MOCK_CLASSROOMS.filter(c => {
    const matchesTab = c.type === activeTab;
    const matchesYear = c.academicYear === selectedYearId;
    const matchesSearch = c.name.toLowerCase().includes(searchQuery.toLowerCase()) || c.grade.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTab && matchesYear && matchesSearch;
  });
  const handleCreateClassroom = async (data: any) => {
    console.log('Creating classroom:', data);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsCreateModalOpen(false);
  };
  // Selection Handlers
  const toggleSelectionMode = () => {
    setIsSelectionMode(!isSelectionMode);
    setSelectedClassroomIds(new Set()); // Clear selection when toggling
  };
  const toggleClassroomSelection = (id: string) => {
    const newSelected = new Set(selectedClassroomIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedClassroomIds(newSelected);
  };
  const handleBulkArchive = async () => {
    console.log('Archiving classrooms:', Array.from(selectedClassroomIds));
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsBulkArchiveModalOpen(false);
    // Open recreate modal after successful archive
    setIsRecreateModalOpen(true);
  };
  const handleRecreateClassrooms = async (yearId: string) => {
    console.log('Recreating classrooms:', Array.from(selectedClassroomIds), 'for year:', yearId);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    setIsRecreateModalOpen(false);
    setIsSelectionMode(false);
    setSelectedClassroomIds(new Set());
    // Optionally switch to the new year to show created classrooms
    if (yearId !== selectedYearId) {
      setSelectedYearId(yearId);
    }
  };
  // Individual Classroom Action Handlers
  const handleRenameClassroom = (id: string, currentName: string) => {
    const classroom = MOCK_CLASSROOMS.find(c => c.id === id);
    if (classroom) {
      setClassroomToRename(classroom);
      setIsRenameModalOpen(true);
    }
  };
  const handleSaveRename = async (data: any) => {
    console.log('Renaming classroom:', classroomToRename?.id, 'to:', data.name);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsRenameModalOpen(false);
    setClassroomToRename(null);
  };
  const handleDuplicateClassroom = (id: string) => {
    const classroom = MOCK_CLASSROOMS.find(c => c.id === id);
    console.log('Duplicating classroom:', classroom);
    // In real implementation, this would open a modal or create a copy
    alert(`Duplicating classroom: ${classroom?.name}`);
  };
  const handleArchiveClassroom = (id: string) => {
    const classroom = MOCK_CLASSROOMS.find(c => c.id === id);
    if (classroom) {
      setClassroomToArchive(classroom);
      setIsArchiveConfirmOpen(true);
    }
  };
  const confirmArchiveClassroom = async () => {
    console.log('Archiving classroom:', classroomToArchive?.id);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    setIsArchiveConfirmOpen(false);
    setClassroomToArchive(null);
  };
  return <div className="flex flex-col min-h-screen bg-background" data-id="element-2532">
      <Header data-id="element-2533" />
      <div className="flex flex-1 overflow-hidden" data-id="element-2534">
        <div className="hidden md:block" data-id="element-2535">
          <Sidebar data-id="element-2536" />
        </div>
        <div className="flex-1 flex flex-col min-w-0 w-full overflow-y-auto" data-id="element-2537">
          <main className="flex-1 pb-24" data-id="element-2538">
            <div className="max-w-7xl mx-auto px-6 py-8" data-id="element-2539">
              {/* Header Section */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8" data-id="element-2540">
                <div data-id="element-2541">
                  <h1 className="text-3xl font-bold text-text-primary mb-2" data-id="element-2542">
                    Classrooms
                  </h1>
                  <p className="text-text-secondary" data-id="element-2543">
                    Manage official cohorts and flexible educator groups
                  </p>
                </div>

                <div className="flex items-center gap-3" data-id="element-2544">
                  <AcademicYearSelector years={MOCK_YEARS} selectedYearId={selectedYearId} onYearChange={setSelectedYearId} data-id="element-2545" />

                  {/* Selection Mode Toggle */}
                  {filteredClassrooms.length > 0 && <button onClick={toggleSelectionMode} className={`flex items-center gap-2 px-4 py-2.5 border border-custom rounded-xl font-semibold transition-all ${isSelectionMode ? 'bg-surface-elevated text-text-primary shadow-sm' : 'bg-white text-text-secondary hover:text-text-primary hover:bg-gray-50'}`} data-id="element-2546">
                      <CheckSquareIcon size={20} className={isSelectionMode ? 'text-[#00B75F]' : ''} data-id="element-2547" />
                      {isSelectionMode ? 'Cancel Selection' : 'Select'}
                    </button>}

                  <button onClick={() => setIsCreateModalOpen(true)} className="flex items-center gap-2 px-4 py-2.5 bg-[#00B75F] text-white rounded-xl font-semibold hover:bg-[#00a055] transition-all shadow-sm" data-id="element-2548">
                    <PlusIcon size={20} data-id="element-2549" />
                    Create Classroom
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex items-center gap-6 border-b border-custom mb-6" data-id="element-2550">
                <button onClick={() => setActiveTab('organization')} className={`pb-3 text-sm font-semibold transition-all relative ${activeTab === 'organization' ? 'text-[#00B75F]' : 'text-text-secondary hover:text-text-primary'}`} data-id="element-2551">
                  Organization Classrooms
                  {activeTab === 'organization' && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#00B75F]" data-id="element-2552" />}
                </button>
                <button onClick={() => setActiveTab('private')} className={`pb-3 text-sm font-semibold transition-all relative ${activeTab === 'private' ? 'text-[#00B75F]' : 'text-text-secondary hover:text-text-primary'}`} data-id="element-2553">
                  Groups
                  {activeTab === 'private' && <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#00B75F]" data-id="element-2554" />}
                </button>
              </div>

              {/* Filters & Search */}
              <div className="flex items-center gap-3 mb-6" data-id="element-2555">
                <div className="relative flex-1 max-w-md" data-id="element-2556">
                  <SearchIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary" data-id="element-2557" />
                  <input type="text" placeholder="Search classrooms..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-white border border-custom rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#00B75F]/20 focus:border-[#00B75F]" data-id="element-2558" />
                </div>
                <button className="p-2.5 bg-white border border-custom rounded-xl text-text-secondary hover:text-text-primary hover:bg-gray-50 transition-colors" data-id="element-2559">
                  <FilterIcon size={18} data-id="element-2560" />
                </button>
              </div>

              {/* Grid Content */}
              {filteredClassrooms.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-2561">
                  {filteredClassrooms.map(classroom => <ClassroomCard key={classroom.id} {...classroom} isSelectionMode={isSelectionMode} isSelected={selectedClassroomIds.has(classroom.id)} onToggleSelect={toggleClassroomSelection} onRename={handleRenameClassroom} onDuplicate={handleDuplicateClassroom} onArchive={handleArchiveClassroom} data-id="element-2562" />)}
                </div> : <EmptyState icon={<SchoolIcon size={48} className="text-text-tertiary" data-id="element-2564" />} title={activeTab === 'organization' ? 'No classrooms found' : 'No groups found'} description={activeTab === 'organization' ? 'Create your first classroom to get started with this academic year.' : 'No groups have been created by educators yet. Groups are flexible sets for remedial batches, practice groups, and short-term cohorts.'} actionLabel={activeTab === 'organization' ? 'Create Classroom' : undefined} onAction={activeTab === 'organization' ? () => setIsCreateModalOpen(true) : undefined} data-id="element-2563" />}
            </div>
          </main>
          <Footer data-id="element-2565" />
        </div>
      </div>

      <CreateClassroomModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} onCreate={handleCreateClassroom} academicYears={MOCK_YEARS} defaultYearId={selectedYearId} data-id="element-2566" />

      {/* Rename Modal */}
      {classroomToRename && <EditClassroomModal isOpen={isRenameModalOpen} onClose={() => {
      setIsRenameModalOpen(false);
      setClassroomToRename(null);
    }} onSave={handleSaveRename} classroom={{
      name: classroomToRename.name,
      grade: classroomToRename.grade,
      section: classroomToRename.section
    }} data-id="element-2567" />}

      {/* Archive Confirmation Modal */}
      {classroomToArchive && <ConfirmationModal isOpen={isArchiveConfirmOpen} onClose={() => {
      setIsArchiveConfirmOpen(false);
      setClassroomToArchive(null);
    }} onConfirm={confirmArchiveClassroom} title="Archive Classroom?" description={`This will archive "${classroomToArchive.name}" and end all student enrollments for the current academic year. Student records, enrollment history, and reports are permanently preserved.`} confirmLabel="Archive Classroom" type="warning" data-id="element-2568" />}

      {/* Bulk Actions Components */}
      <BulkActionsBar isOpen={isSelectionMode && selectedClassroomIds.size > 0} selectedCount={selectedClassroomIds.size} onCancel={() => setSelectedClassroomIds(new Set())} onArchive={() => setIsBulkArchiveModalOpen(true)} data-id="element-2569" />

      <BulkArchiveModal isOpen={isBulkArchiveModalOpen} onClose={() => setIsBulkArchiveModalOpen(false)} onConfirm={handleBulkArchive} count={selectedClassroomIds.size} data-id="element-2570" />

      <RecreateClassroomsModal isOpen={isRecreateModalOpen} onClose={() => setIsRecreateModalOpen(false)} onConfirm={handleRecreateClassrooms} count={selectedClassroomIds.size} academicYears={MOCK_YEARS} currentYearId={selectedYearId} data-id="element-2571" />
    </div>;
}